import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import { useAppQuery } from "../../../../http/useAppQuery.ts";
import { createStorageStore } from "../../../../utils/createStore.ts";
import { TOOL_TO_GENERATION_TYPE } from "../../../Board/constants.ts";
import type {
  ModelActions,
  ModelCompatibleTool,
  ModelGenerationType,
  ModelsSettings,
} from "../../../Board/types.ts";
import { useDebugMode } from "../../../components/DebugMode/hooks/useDebugMode.ts";
import {
  CHARACTER_MODEL_TYPE,
  GENERIC_STYLE_MODEL_UUID,
  OBJECT_MODEL_TYPE,
} from "../../../constants.ts";
import { useModels } from "../../../hooks/useModels.ts";
import type {
  ModelType,
  ModelWithIsCompatibleAndTags,
} from "../../../types.ts";
import {
  createModelsGenerationParamsSchema,
  type ModelExtraGenerationParams,
  type ModelGenerationParams,
} from "./types.ts";

export const selectedModelsStorageFactory = (
  key: string,
  version: number,
  modelGenerationTypes: ModelGenerationType[],
  getModelDefaultGenerationParams: (
    modelUuid: string,
    isModelTrained: boolean,
  ) => ModelGenerationParams,
  updateModelGenerationParams: (
    modelUuid: string,
    allowedModelGenerationTypes: ModelGenerationType[],
    isModelTrained: boolean,
    modelGenerationType: ModelGenerationType,
  ) => ModelGenerationParams,
) => {
  const generationParamsStore = createStorageStore(
    localStorage,
    `${key}-v${version}`,
    z.record(
      createModelsGenerationParamsSchema(modelGenerationTypes).optional(),
    ),
    {},
    ({ update }) => ({
      addModelToSelection: ({
        boardUuid,
        modelUuid,
        isModelTrained,
      }: {
        boardUuid: string;
        modelUuid: string;
        isModelTrained: boolean;
      }) =>
        update((draft) => {
          // XXX: We exclude the generic style model from the selection as it is the default model
          if (modelUuid === GENERIC_STYLE_MODEL_UUID) return;
          draft[boardUuid] ??= [];
          const currentModels = draft[boardUuid].filter(
            (it) => it.uuid !== modelUuid,
          );

          draft[boardUuid] = [
            ...currentModels,
            getModelDefaultGenerationParams(modelUuid, isModelTrained),
          ];
        }),
      updateModelGenerationType: ({
        boardUuid,
        modelUuid,
        isModelTrained,
        modelGenerationType,
      }: {
        boardUuid: string;
        modelUuid: string;
        isModelTrained: boolean;
        modelGenerationType: ModelGenerationType;
      }) =>
        update((draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const currentModels = draft[boardUuid]!.filter(
            (it) => it.uuid !== modelUuid,
          );
          const currentModelGenerationParams = draft[boardUuid]?.find(
            (it) => it.uuid === modelUuid,
          );
          if (!currentModelGenerationParams) return;
          const newModelGenerationParams = updateModelGenerationParams(
            modelUuid,
            modelGenerationTypes,
            isModelTrained,
            modelGenerationType,
          );
          draft[boardUuid] = [...currentModels, newModelGenerationParams];
        }),
      updateModelGenerationParams: ({
        boardUuid,
        modelUuid,
        extraModelGenerationParams,
      }: {
        boardUuid: string;
        modelUuid: string;
        extraModelGenerationParams: Partial<ModelExtraGenerationParams>;
      }) =>
        update((draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const currentModels = draft[boardUuid]!.filter(
            (it) => it.uuid !== modelUuid,
          );
          const currentModelGenerationParams = draft[boardUuid]?.find(
            (it) => it.uuid === modelUuid,
          );

          if (!currentModelGenerationParams) return;

          draft[boardUuid] = [
            ...currentModels,
            {
              ...currentModelGenerationParams,
              ...extraModelGenerationParams,
            },
          ];
        }),
      setIsDebugMode: ({
        boardUuid,
        modelUuid,
      }: {
        boardUuid: string;
        modelUuid: string;
      }) =>
        update((draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const currentModels = draft[boardUuid]!.filter(
            (it) => it.uuid !== modelUuid,
          );
          const currentModelGenerationParams = draft[boardUuid]?.find(
            (it) => it.uuid === modelUuid,
          );

          if (!currentModelGenerationParams) return;

          draft[boardUuid] = [
            ...currentModels,
            {
              ...currentModelGenerationParams,
            },
          ];
        }),
      unsetIsDebugMode: ({
        boardUuid,
        modelUuid,
        isModelTrained,
      }: {
        boardUuid: string;
        modelUuid: string;
        isModelTrained: boolean;
      }) =>
        update((draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const currentModels = draft[boardUuid]!.filter(
            (it) => it.uuid !== modelUuid,
          );
          const currentModelGenerationParams = draft[boardUuid]?.find(
            (it) => it.uuid === modelUuid,
          );
          if (!currentModelGenerationParams) return;

          draft[boardUuid] = [
            ...currentModels,
            getModelDefaultGenerationParams(modelUuid, isModelTrained),
          ];
        }),

      removeModelFromSelection: ({
        boardUuid,
        modelUuid,
      }: {
        boardUuid: string;
        modelUuid: string;
      }) =>
        update((draft) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const currentModels = draft[boardUuid]!.filter(
            (it) => it.uuid !== modelUuid,
          );
          draft[boardUuid] = [...currentModels];
        }),
      resetModelSelections: ({ boardUuid }: { boardUuid: string }) =>
        update((draft) => {
          draft[boardUuid] = [];
        }),
    }),
  );

  const useAllModels = (
    boardUuid: string,
    tool: ModelCompatibleTool,
    excludeModelUuid?: string,
  ): ModelWithIsCompatibleAndTags[] => {
    const selectedModelsUuids = generationParamsStore.useState(
      (s) => s[boardUuid],
    );
    const baseReferenceUuids = selectedModelsUuids?.map((it) => it.uuid);

    const { models: data, isLoading } = useModels({
      tool,
      baseReferenceUuids: excludeModelUuid
        ? baseReferenceUuids?.filter((it) => it !== excludeModelUuid)
        : baseReferenceUuids,
    });
    return isLoading ? [] : data;
  };

  const useModelsSettings = (
    boardUuid: string,
    tool: ModelCompatibleTool,
  ): ModelsSettings => {
    const generationParams = generationParamsStore.useState(
      (s) => s[boardUuid],
    );

    const { models: data, isLoading } = useModels({
      tool,
      baseReferenceUuids: generationParams?.map((it) => it.uuid),
    });
    const models = useMemo(
      () =>
        data.filter(
          (model) =>
            generationParams?.map((it) => it.uuid).includes(model.uuid),
        ),
      [data, generationParams],
    );

    const isDebugModeOn = useDebugMode();

    const setIsDebugMode = useCallback(
      (modelUuid: string) => {
        generationParamsStore.setIsDebugMode({
          boardUuid,
          modelUuid,
        });
      },
      [boardUuid],
    );

    const unsetIsDebugMode = useCallback(
      (modelUuid: string, isModelTrained: boolean) => {
        generationParamsStore.unsetIsDebugMode({
          boardUuid,
          modelUuid,
          isModelTrained,
        });
      },
      [boardUuid],
    );

    useEffect(() => {
      if (isDebugModeOn) {
        for (const model of models) {
          setIsDebugMode(model.uuid);
        }
      } else {
        for (const model of models) {
          unsetIsDebugMode(model.uuid, model.is_trained);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDebugModeOn, setIsDebugMode, unsetIsDebugMode]);

    useEffect(() => {
      if (generationParams === undefined || isLoading) return;

      for (const selectedModelsGenerationParam of generationParams) {
        const model = models.find(
          (it) => it.uuid === selectedModelsGenerationParam.uuid,
        );
        if (model === undefined) {
          generationParamsStore.removeModelFromSelection({
            boardUuid,
            modelUuid: selectedModelsGenerationParam.uuid,
          });
        }
      }
    }, [boardUuid, models, generationParams, isLoading]);

    return {
      generationParams:
        generationParamsStore.useState((m) => m[boardUuid]) ?? [],
      models,
      hasExactlyOneCharacterModelSelected:
        models.length === 1 &&
        models.every((it) => it.type === CHARACTER_MODEL_TYPE),
      hasExactlyOneObjectModelSelected:
        models.length === 1 &&
        models.every((it) => it.type === OBJECT_MODEL_TYPE),
    };
  };

  const modelActions = (boardUuid: string): ModelActions => ({
    addModel: (modelUuid: string, isModelTrained: boolean) => {
      generationParamsStore.addModelToSelection({
        boardUuid,
        modelUuid,
        isModelTrained,
      });
    },
    removeModel: (modelUuid: string) => {
      generationParamsStore.removeModelFromSelection({
        boardUuid,
        modelUuid,
      });
    },
    updateModelGenerationParams: (
      modelUuid: string,
      extraModelGenerationParams: Partial<ModelExtraGenerationParams>,
    ) => {
      generationParamsStore.updateModelGenerationParams({
        boardUuid,
        modelUuid,
        extraModelGenerationParams,
      });
    },
    updateModelGenerationType: (
      modelUuid: string,
      isModelTrained: boolean,
      modelGenerationType: ModelGenerationType,
    ) => {
      generationParamsStore.updateModelGenerationType({
        boardUuid,
        modelUuid,
        isModelTrained,
        modelGenerationType,
      });
    },
    reset: () => {
      generationParamsStore.resetModelSelections({ boardUuid });
    },
  });

  const useNumberOfOtherCompatibleModels = (
    boardUuid: string,
    tool: ModelCompatibleTool,
    modelType: ModelType | undefined,
  ) => {
    const generationParams = generationParamsStore.useState(
      (s) => s[boardUuid],
    );
    const selectedModelsUuids = generationParams?.map((it) => it.uuid);
    const models = useAllModels(boardUuid, tool);
    return models.filter(
      (it) =>
        (modelType === undefined || it.type === modelType) &&
        it.is_compatible &&
        !selectedModelsUuids?.includes(it.uuid),
    ).length;
  };

  const useAreSelectedModelsValid = (
    boardUuid: string,
    tool: ModelCompatibleTool,
  ) => {
    const generationParams = generationParamsStore.useState(
      (s) => s[boardUuid],
    );
    const baseReferenceUuids = generationParams?.map((it) => it.uuid);
    const { data } = useAppQuery<{ is_compatible: boolean }>({
      queryKey: "references-compatibility",
      queryParamsKey: {
        generation_type: TOOL_TO_GENERATION_TYPE[tool],
        ...(baseReferenceUuids && { base_reference_uuids: baseReferenceUuids }),
      },
    });
    return data?.is_compatible ?? true;
  };

  return {
    generationParamsStore,
    useModelsSettings,
    modelActions,
    useAllModels,
    useNumberOfOtherCompatibleModels,
    useAreSelectedModelsValid,
  };
};
